import React, { useState, useEffect } from 'react';
import { Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, UncontrolledTooltip } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { ThumbsUp, ThumbsDown, Save, Share, MessageSquare, Eye } from 'react-feather';
import { connect } from 'react-redux';
import { getBrandEngagements as getBrandEngagementsAction, getRetailerEngagements as getRetailerEngagementsAction } from '../../ReduxToolkit/Reducers/brands';

const TABS = [
  { id: 'like', label: 'Likes', icon: <ThumbsUp size={16} /> },
  { id: 'dislike', label: 'Dislikes', icon: <ThumbsDown size={16} /> },
  { id: 'save', label: 'Saves', icon: <Save size={16} /> },
  { id: 'share', label: 'Shares', icon: <Share size={16} /> },
  { id: 'comment', label: 'Comments', icon: <MessageSquare size={16} /> },
  { id: 'view', label: 'Views', icon: <Eye size={16} /> },
];

function ProfileEngagementsTab({ profileId, type, getBrandEngagements, getRetailerEngagements, engagements, totalEngagements, isLoading }) {
  const [activeTab, setActiveTab] = useState('like');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (profileId && activeTab && type) {
      fetchEngagements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, activeTab, page, itemsPerPage, type]);

  const fetchEngagements = async () => {
    if (type === 'brand') {
      await getBrandEngagements(profileId, activeTab, page, itemsPerPage);
    } else {
      await getRetailerEngagements(profileId, activeTab, page, itemsPerPage);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleItemPerPageSelect = (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  const engagementColumns = [
    {
      name: 'Date',
      selector: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      width: '180px',
    },
    {
      name: 'Source Type',
      selector: (row) => row.source_type || 'N/A',
      sortable: true,
      width: '120px',
      cell: (row) => (
        <span style={{ textTransform: 'capitalize' }}>{row.source_type || 'N/A'}</span>
      ),
    },
    {
      name: 'Source',
      selector: (row) => {
        if (row.source_type === 'brand') {
          return row.source_brand?.name || 'N/A';
        } else if (row.source_type === 'retailer') {
          return row.source_retailer?.name || 'N/A';
        }
        return 'N/A';
      },
      sortable: true,
      width: '200px',
    },
    {
      name: 'Target Type',
      selector: (row) => row.target_type || 'N/A',
      sortable: true,
      width: '120px',
      cell: (row) => (
        <span style={{ textTransform: 'capitalize' }}>{row.target_type || 'N/A'}</span>
      ),
    },
    {
      name: 'Target',
      selector: (row) => {
        if (row.target_type === 'brand') {
          return row.target_brand?.name || 'N/A';
        } else if (row.target_type === 'retailer') {
          return row.target_retailer?.name || 'N/A';
        }
        return 'N/A';
      },
      sortable: true,
      width: '200px',
    },
    {
      name: 'Country',
      selector: (row) => {
        if (row.target_type === 'brand') {
          return row.target_brand?.country || 'N/A';
        } else if (row.target_type === 'retailer') {
          return row.target_retailer?.country || 'N/A';
        }
        return 'N/A';
      },
      sortable: true,
      width: '120px',
    },
    {
      name: 'Organization',
      selector: (row) => row.organization?.name || 'N/A',
      sortable: true,
      width: '200px',
    },
  ];

  const commentColumn = {
    name: 'Comment',
    selector: (row) => row.comment || 'N/A',
    sortable: false,
    width: '250px',
    cell: (row) => (
      <div style={{ whiteSpace: 'normal', padding: '8px 0' }}>
        {row.comment || 'N/A'}
      </div>
    ),
  };

  const dislikeColumns = [
    {
      name: 'Date',
      selector: (row) => row.updated_at ? new Date(row.updated_at).toLocaleString() : 'N/A',
      sortable: true,
      width: '180px',
    },
    {
      name: 'Brand',
      selector: (row) => row.brand?.name || 'N/A',
      sortable: true,
      width: '200px',
    },
    {
      name: 'Brand Country',
      selector: (row) => row.brand?.country || 'N/A',
      sortable: true,
      width: '150px',
    },
    {
      name: 'Retailer',
      selector: (row) => row.retailer?.name || 'N/A',
      sortable: true,
      width: '200px',
    },
    {
      name: 'Retailer Country',
      selector: (row) => row.retailer?.country || 'N/A',
      sortable: true,
      width: '150px',
    },
    {
      name: 'Match Rate',
      selector: (row) => row.match_rate || 'N/A',
      sortable: true,
      width: '120px',
      cell: (row) => (
        <span>{row.match_rate ? `${row.match_rate}%` : 'N/A'}</span>
      ),
    },
    {
      name: 'Grade',
      selector: (row) => row.grade || 'N/A',
      sortable: true,
      width: '100px',
    },
    {
      name: 'Matched by AI',
      selector: (row) => row.matched_by_ai !== null ? (row.matched_by_ai ? 'Yes' : 'No') : 'N/A',
      sortable: true,
      width: '130px',
    },
    {
      name: 'Feedback',
      selector: (row) => row.note || 'N/A',
      sortable: false,
      width: '250px',
      cell: (row, index) => {
        const note = row.note || 'N/A';
        const tooltipId = `feedback-tooltip-org-${row.brand_id}-${row.retailer_id}-${index}`;
        const truncatedText = note.length > 50 ? `${note.substring(0, 50)}...` : note;

        return (
          <div style={{ padding: '8px 0' }}>
            {note !== 'N/A' && note.length > 50 ? (
              <>
                <span id={tooltipId} style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                  {truncatedText}
                </span>
                <UncontrolledTooltip
                  placement="top"
                  target={tooltipId}
                  style={{
                    maxWidth: '400px',
                    backgroundColor: '#333',
                    padding: '10px',
                    fontSize: '13px',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word'
                  }}
                >
                  {note}
                </UncontrolledTooltip>
              </>
            ) : (
              <span>{note}</span>
            )}
          </div>
        );
      },
    },
  ];

  const getColumns = () => {
    if (activeTab === 'dislike') {
      return dislikeColumns;
    } else if (activeTab === 'comment') {
      return [...engagementColumns, commentColumn];
    } else {
      return engagementColumns;
    }
  };

  const currentData = engagements[activeTab]?.[page] || [];
  const currentTotal = totalEngagements[activeTab] || 0;

  return (
    <Card className="mt-3">
      <CardBody>
        <Nav tabs className="mb-3">
          {TABS.map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => handleTabChange(tab.id)}
                style={{ cursor: 'pointer' }}
              >
                <span className="me-1">{tab.icon}</span>
                {tab.label}
                {currentTotal > 0 && activeTab === tab.id && (
                  <span className="badge bg-primary ms-2">{currentTotal}</span>
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId={activeTab}>
            <DataTable
              columns={getColumns()}
              data={currentData}
              progressPending={isLoading}
              pagination
              paginationServer
              paginationTotalRows={currentTotal}
              paginationDefaultPage={page}
              onChangePage={(newPage) => setPage(newPage)}
              onChangeRowsPerPage={handleItemPerPageSelect}
              paginationPerPage={itemsPerPage}
              highlightOnHover
              striped
              responsive
              noDataComponent={
                <div className="text-center py-5">
                  <p className="text-muted">No {TABS.find(t => t.id === activeTab)?.label.toLowerCase()} found</p>
                </div>
              }
              customStyles={{
                tableWrapper: {
                  style: {
                    minHeight: '400px',
                  },
                },
              }}
            />
          </TabPane>
        </TabContent>
      </CardBody>
    </Card>
  );
}

const mapStateToProps = (state) => ({
  engagements: state.brands.engagements || {},
  totalEngagements: state.brands.totalEngagements || {},
  isLoading: state.brands.isLoading,
});

const mapDispatchToProps = {
  getBrandEngagements: getBrandEngagementsAction,
  getRetailerEngagements: getRetailerEngagementsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEngagementsTab);
